import {Injectable, OnDestroy} from "@angular/core";
import {Subscription} from "rxjs";
import {ValidationMessage} from "@fedelta-pos/data-client";
import {SessionModel} from "../orchestration/session.model";
import {UUID} from "angular2-uuid";
import {OrderModel} from "../order/order.model";
import {OrderingContextModel} from "../ordering-context/ordering-context.model";
import {OrchestrationEvents} from "../orchestration/orchestration-events";
import {GroupOrderEvents} from "./group-order-events";
import {OrderEvents, OrderInitiationStatus, OrderSubmissionStatus} from "../order/order-events";
import {NavigationService} from "../orchestration/navigation.service";
import {GroupOrderPageMode} from "./group-order-page/group-order-page-mode";
import {IdentityModel} from "../identity/identity.model";
import {OrderingPhase, SessionEvents} from "../orchestration/session-events";
import {LoadingService} from "../ui-elements/loading/loading.service";
import {MenuEvents} from "../menu/menu-events";
import {ActiveSchedulesSearchModel} from "../schedule/active-schedules-search.model";
import {
    OrderTransferTargetData,
    OrderTransferTargetEntityModel,
    OrderTransferTargetReferenceData
} from "./order-transfer-target-entity.model";
import {OrderTransferTargetSearchModel} from "./order-transfer-target-search.model";
import {OrderTransferTargetStatus} from "./order-transfer-request-poll.service";
import {OrderType} from "../order/order";

@Injectable()
export class GroupOrderModel implements OnDestroy {
    private static readonly ORDER_TRANSFER_POLL_INTERVAL: number = 5000;
    public static readonly BEACON_SCAN_TIMEOUT: number = 1000 * 60 * 60;

    public selectedTransferTarget: OrderTransferTargetReferenceData;
    public preferredTransferTarget: OrderTransferTargetReferenceData;
    public lastTransferTargetProposed: number = 0;

    private openingGroupOrder: boolean = false;
    private subscriptions: Subscription = new Subscription();

    constructor(private sessionModel: SessionModel,
                private orderingContextModel: OrderingContextModel,
                private orderModel: OrderModel,
                private identityModel: IdentityModel,
                private activeSchedulesSearchModel: ActiveSchedulesSearchModel,
                private orderTransferTargetEntityModel: OrderTransferTargetEntityModel,
                private orderTransferTargetSearchModel: OrderTransferTargetSearchModel,
                private loadingService: LoadingService,
                private navigationService: NavigationService) {
        console.log("--- GroupOrderModel");

        this.subscriptions.add(OrchestrationEvents.sessionReset$.subscribe(() => {
            // noinspection JSIgnoredPromiseFromCall
            this.closeGroupOrder(OrderTransferTargetStatus.CLOSED);
            this.selectedTransferTarget = undefined;
        }));

        this.subscriptions.add(SessionEvents.orderingPhase$.subscribe(async (orderingPhase: OrderingPhase) => {
            if (orderingPhase === OrderingPhase.PAYMENT && this.orderModel.initiatedOrder && this.orderModel.initiatedOrder.availableTransferTargets.length) {
                this.lastTransferTargetProposed = this.orderModel.initiatedOrder.availableTransferTargets[0].created.getTime();
            }
        }));

        this.subscriptions.add(OrderEvents.orderInitiationStatus$.subscribe((status: OrderInitiationStatus) => {
            if (status === OrderInitiationStatus.INITIATED) {
                if (this.orderModel.initiatedOrder.availableTransferTargets.length) {
                    this.preferredTransferTarget = this.selectedTransferTarget ? this.orderModel.initiatedOrder.availableTransferTargets
                        .find((target: OrderTransferTargetReferenceData) => target.id === this.selectedTransferTarget.id) : undefined;
                    if (!this.preferredTransferTarget) {
                        this.preferredTransferTarget = this.orderModel.initiatedOrder.availableTransferTargets[0];
                    }
                } else {
                    this.preferredTransferTarget = undefined;
                }
            }
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public get currentOrderTransferTargetEntityModel(): OrderTransferTargetEntityModel {
        return this.orderTransferTargetEntityModel;
    }

    public get canHaveGroupOrder(): boolean {
        return !this.sessionModel.waiterMode && !!this.sessionModel.beaconId &&
            ((this.orderingContextModel.orderType === OrderType.ORDER_AT_TABLE && this.orderingContextModel.currentStore.allowTableGroupOrders && !!this.orderingContextModel.currentStore.tableNumber) ||
                (this.orderingContextModel.orderType === OrderType.ORDER_AHEAD && this.orderingContextModel.currentStore.allowPickupGroupOrders));
    }

    public get canStartGroupOrder(): boolean {
        return this.canHaveGroupOrder && (!this.sessionModel.beaconScanTime || (Date.now() - this.sessionModel.beaconScanTime <= GroupOrderModel.BEACON_SCAN_TIMEOUT));
    }

    public get hasGroupOrder(): boolean {
        return this.orderTransferTargetEntityModel.data && !!this.orderTransferTargetEntityModel.data.id;
    }

    public get showGroupOrderNotice(): boolean {
        return this.hasGroupOrder || !!this.orderModel.totalTransfers;
    }

    public async promptForGroupOrderParticipation(): Promise<void> {
        if (!this.sessionModel.waiterMode &&
            !MenuEvents.itemOrderPending$.value &&
            !this.hasGroupOrder &&
            !this.orderModel.totalTransfers &&
            this.orderModel.initiationStatus === OrderInitiationStatus.INITIATED &&
            this.orderModel.initiatedOrder.availableTransferTargets.length &&
            this.orderModel.initiatedOrder.availableTransferTargets[0].created.getTime() > this.lastTransferTargetProposed) {
            if (!this.identityModel.currentIdentity.firstName) {
                this.identityModel.currentIdentity.firstName = this.orderingContextModel.firstName;
            }
            if (await this.navigationService.createPage("woa-group-order-page", {
                mode: GroupOrderPageMode.PARTICIPANTS,
                hideNewGroupOrder: true
            })) {
                this.orderingContextModel.firstName = this.identityModel.currentIdentity.firstName;
                this.orderingContextModel.firstNameTrusted = false;
            }
        }
    }

    public async openGroupOrder(): Promise<void> {
        if (this.openingGroupOrder) {
            return;
        }
        this.openingGroupOrder = true;
        try {
            if (!this.hasGroupOrder && !this.orderModel.totalTransfers) {
                await this.loadingService.performWithSpinner(async () => {
                    this.orderTransferTargetSearchModel.setFilterValue("excludeTargetId", this.orderTransferTargetEntityModel.data ? this.orderTransferTargetEntityModel.data.id : undefined);
                    this.orderTransferTargetSearchModel.setFilterValue("orderType", OrderType.toString(this.orderingContextModel.orderType));
                    this.orderTransferTargetSearchModel.setFilterValue("tableNumber", this.orderingContextModel.currentStore.tableNumber);
                    this.orderTransferTargetSearchModel.setFilterValue("beaconId", this.sessionModel.beaconId);
                    await this.orderTransferTargetSearchModel.retrieve();
                    this.orderModel.initiatedOrder.availableTransferTargets = this.orderTransferTargetSearchModel.data;
                });
                if (this.orderModel.initiatedOrder.availableTransferTargets.length) {
                    if (await this.navigationService.createPage("woa-group-order-page", {mode: GroupOrderPageMode.PARTICIPANTS})) {
                        this.orderingContextModel.firstName = this.identityModel.currentIdentity.firstName;
                        this.orderingContextModel.firstNameTrusted = false;
                    }
                    return;
                }
            }
            return this.navigationService.createPage("woa-group-order-page", {mode: GroupOrderPageMode.EDIT});
        } finally {
            this.openingGroupOrder = false;
        }
    }

    public async saveGroupOrder(creating: boolean = false, groupOrderName?: string, groupOrderId?: string): Promise<ValidationMessage[]> {
        GroupOrderEvents.groupOrderStatus$.next(OrderTransferTargetStatus.SAVING);
        if (!this.hasGroupOrder) {
            creating = true;
        }
        if (creating) {
            this.orderTransferTargetEntityModel.newEntity();
        }
        const target: OrderTransferTargetData = this.orderTransferTargetEntityModel.data;
        if (creating) {
            target.id = groupOrderId || UUID.UUID();
        }
        if (creating || groupOrderName) {
            target.name = groupOrderName;
        }
        target.appStore = this.orderingContextModel.currentStore.webTerminalId;
        target.orderTypeEnum = this.orderingContextModel.orderType;
        target.tableNumber = this.orderingContextModel.currentStore.tableNumber;
        target.beaconId = this.sessionModel.beaconId;

        try {
            const validationMessages: ValidationMessage[] = await this.orderTransferTargetEntityModel.saveEntity();
            if (validationMessages) {
                if (creating) {
                    this.orderTransferTargetEntityModel.data.id = undefined;
                    GroupOrderEvents.currentGroupOrder$.next(undefined);
                    GroupOrderEvents.groupOrderStatus$.next(OrderTransferTargetStatus.CLOSED);
                }
                return validationMessages;
            }
            GroupOrderEvents.currentGroupOrder$.next(this.orderTransferTargetEntityModel.data);
            GroupOrderEvents.groupOrderStatus$.next(OrderTransferTargetStatus.OPEN);

            // Force order initiation to persist group order details locally in current order
            this.orderModel.requestOrderInitiation("save group order");

            setTimeout(() => this.pollOrderTransferRequests(), GroupOrderModel.ORDER_TRANSFER_POLL_INTERVAL);
            return undefined;
        } catch (error) {
            if (creating) {
                this.orderTransferTargetEntityModel.data.id = undefined;
                GroupOrderEvents.currentGroupOrder$.next(undefined);
                GroupOrderEvents.groupOrderStatus$.next(OrderTransferTargetStatus.CLOSED);
            }
            throw error;
        }
    }

    public async closeGroupOrder(closeStatus: OrderTransferTargetStatus, closeOnServer: boolean = true, removeItems: boolean = false): Promise<void> {
        if (this.hasGroupOrder || !!this.orderModel.totalTransfers) {
            if (this.hasGroupOrder) {
                if (closeOnServer) {
                    await this.orderTransferTargetEntityModel.deleteEntity(this.orderTransferTargetEntityModel.data.id);
                }
                this.orderTransferTargetEntityModel.data.id = undefined;
                GroupOrderEvents.currentGroupOrder$.next(undefined);
            } else {
                closeOnServer = false;
            }

            const itemsRemoved: boolean = removeItems && this.orderModel.removeTransferRequestOrderItems();

            // Request order initiation to persist removal of group order details locally in current order
            if (closeOnServer && !itemsRemoved) {
                this.orderModel.requestOrderInitiation("close group order");
            }
        }
        if (GroupOrderEvents.groupOrderStatus$.getValue() !== closeStatus) {
            GroupOrderEvents.groupOrderStatus$.next(closeStatus);
            if ((closeStatus === OrderTransferTargetStatus.CLOSED_TIMEOUT || closeStatus === OrderTransferTargetStatus.CLOSED_CONFLICT)) {
                setTimeout(() => {
                    if (SessionEvents.orderingPhase$.value === OrderingPhase.ORDERING &&
                        OrderEvents.orderSubmissionStatus$.value !== OrderSubmissionStatus.SUBMITTING &&
                        OrderEvents.orderSubmissionStatus$.value !== OrderSubmissionStatus.COMPLETE) {
                        this.openGroupOrder();
                    }
                });
            }
        }
    }

    public async restoreGroupOrder(): Promise<ValidationMessage[]> {
        if (this.hasGroupOrder) {
            return this.saveGroupOrder();
        }
    }

    private async pollOrderTransferRequests() {
        if (this.hasGroupOrder) {

            // Pause actual polling of the group order at the payment phase, but keep waiting until the group order is actually closed, as the payment could fail, or the user could go back from the checkout page.
            if (this.canPollOrderTransfer) {
                const status: OrderTransferTargetStatus = await this.orderModel.pollTransferRequests(
                    this.orderTransferTargetEntityModel.data.id,
                    this.orderingContextModel.currentStore.tableNumber,
                    this.sessionModel.beaconId);
                if (this.canPollOrderTransfer && status !== OrderTransferTargetStatus.OPEN && GroupOrderEvents.groupOrderStatus$.getValue() === OrderTransferTargetStatus.OPEN) {
                    // noinspection ES6MissingAwait
                    this.closeGroupOrder(status, false);
                }
            }

            if (this.hasGroupOrder) {
                setTimeout(() => this.pollOrderTransferRequests(), GroupOrderModel.ORDER_TRANSFER_POLL_INTERVAL);
            }
        }
    }

    private get canPollOrderTransfer(): boolean {
        return SessionEvents.orderingPhase$.value === OrderingPhase.ORDERING &&
            !this.orderModel.hasOrderPlacement &&
            GroupOrderEvents.groupOrderStatus$.getValue() !== OrderTransferTargetStatus.SAVING;
    }
}



// WEBPACK FOOTER //
// ./src/app/group-order/group-order.model.ts